/* Creative ERP bilingual login enhancer v44.51.0.
 * Authentication remains owned by Frappe. This runtime owns presentation,
 * language direction, focus treatment and the accessible password reveal.
 */
(() => {
  'use strict';

  const VERSION = window.CPS_THEME_VERSION || 'unknown';
  const FONT_STACKS = Object.freeze({
    Inter: 'Inter, "Segoe UI", system-ui, sans-serif',
    'System UI': '"Segoe UI", system-ui, -apple-system, BlinkMacSystemFont, sans-serif',
    'IBM Plex Sans': '"IBM Plex Sans", "Segoe UI", system-ui, sans-serif',
    'Source Sans Pro': '"Source Sans 3", "Source Sans Pro", "Segoe UI", system-ui, sans-serif',
    Poppins: 'Poppins, "Segoe UI", system-ui, sans-serif',
    Nunito: 'Nunito, "Segoe UI", system-ui, sans-serif',
    Cairo: 'Cairo, "Noto Sans Arabic", Tahoma, "Segoe UI", sans-serif',
    Tajawal: 'Tajawal, "Noto Sans Arabic", Tahoma, "Segoe UI", sans-serif',
  });
  const RTL_LANGUAGES = new Set(['ar', 'fa', 'he', 'ur']);

  let systemModeCleanup = null;
  let slideshowCleanup = null;
  let eventsController = null;

  function root() {
    return document.querySelector('.creative-login-page.cps-auth-shell-host');
  }

  function translate(value) {
    try {
      return typeof window.__ === 'function' ? window.__(value) : value;
    } catch (error) {
      return value;
    }
  }

  function setModeFromHash() {
    const hash = (window.location.hash || '#login').replace(/^#/, '') || 'login';
    if (document.body) document.body.dataset.cpsAuthMode = hash;
  }

  function resolveMode(configured, query) {
    if (configured === 'Dark') return 'dark';
    if (configured === 'System' && query?.matches) return 'dark';
    return 'light';
  }

  function normalizeHex(value, fallback = '#7C3AED') {
    const raw = String(value || '').trim();
    const short = /^#([0-9a-f]{3})$/i.exec(raw);
    const full = /^#([0-9a-f]{6})$/i.exec(raw);
    if (short) return `#${short[1].split('').map((part) => part + part).join('').toLowerCase()}`;
    if (full) return `#${full[1].toLowerCase()}`;
    return fallback;
  }

  function relativeLuminance(hex) {
    const normalized = normalizeHex(hex).slice(1);
    const channels = [0, 2, 4].map((offset) => Number.parseInt(normalized.slice(offset, offset + 2), 16) / 255)
      .map((channel) => channel <= 0.03928 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4);
    return 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
  }

  function primaryContrast(hex) {
    const luminance = relativeLuminance(hex);
    const whiteRatio = 1.05 / (luminance + 0.05);
    const darkRatio = (luminance + 0.05) / 0.05;
    return whiteRatio >= darkRatio ? '#ffffff' : '#111827';
  }

  function colorChannels(value, fallback = '124 58 237') {
    const raw = String(value || '').trim();
    const short = /^#([0-9a-f]{3})$/i.exec(raw);
    const full = /^#([0-9a-f]{6})$/i.exec(raw);
    const hex = short ? short[1].split('').map((part) => part + part).join('') : full?.[1];
    if (!hex) return fallback;
    return [0, 2, 4].map((offset) => Number.parseInt(hex.slice(offset, offset + 2), 16)).join(' ');
  }

  function applyBackground(url) {
    const html = document.documentElement;
    if (!url) {
      html.style.removeProperty('--cps-auth-custom-bg');
      return;
    }
    html.style.setProperty('--cps-auth-custom-bg', `url(${JSON.stringify(url)})`);
  }

  function parseSlides(host) {
    if (String(host?.dataset?.cpsBackgroundMode || '') !== 'Slideshow') return [];
    try {
      const values = JSON.parse(host?.dataset?.cpsSlides || '[]');
      return [...new Set((Array.isArray(values) ? values : []).map((value) => String(value || '').trim()).filter(Boolean))].slice(0, 10);
    } catch (error) { return []; }
  }

  function preloadImage(url) {
    if (!url) return Promise.resolve(false);
    return new Promise((resolve) => {
      const image = new Image();
      image.decoding = 'async';
      try { image.fetchPriority = 'low'; } catch (error) { /* optional */ }
      image.onload = () => resolve(true);
      image.onerror = () => resolve(false);
      image.src = url;
    });
  }

  function setupSlideshow(host, shellMode) {
    slideshowCleanup?.();
    slideshowCleanup = null;
    if (shellMode !== 'nova') return;
    const slides = parseSlides(host);
    if (slides.length < 2 || window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) return;
    // The hero is intentionally hidden on compact login layouts; do not run a
    // timer or decode images that cannot be painted.
    if (window.matchMedia?.('(max-width: 900px)')?.matches) return;
    const hero = host.querySelector('.cps-auth-hero');
    if (!(hero instanceof HTMLElement)) return;

    const layers = [0, 1].map((index) => {
      const layer = document.createElement('span');
      layer.className = `cps-auth-slide-layer cps-auth-slide-layer-${index + 1}`;
      layer.setAttribute('aria-hidden', 'true');
      hero.prepend(layer);
      return layer;
    });
    let active = 0;
    let index = 0;
    layers[0].style.backgroundImage = `url(${JSON.stringify(slides[0])})`;
    layers[0].classList.add('is-active');
    // Server-render the first image for immediate/LCP-safe paint, then hand
    // ownership to the two slideshow layers on the next frame. This avoids
    // double-blending the first image without delaying the login's first paint.
    requestAnimationFrame(() => applyBackground(''));

    let stopped = false;
    let timer = 0;
    const interval = Math.max(5, Math.min(300, Number(host.dataset.cpsSlideInterval) || 12)) * 1000;
    const queuePreload = (url) => {
      const load = () => { if (!stopped) void preloadImage(url); };
      if (typeof requestIdleCallback === 'function') requestIdleCallback(load, {timeout: 1800});
      else window.setTimeout(load, 350);
    };
    const schedule = () => {
      window.clearTimeout(timer);
      if (stopped || document.hidden) return;
      timer = window.setTimeout(advance, interval);
    };
    const advance = async () => {
      if (stopped || document.hidden) return schedule();
      const nextIndex = (index + 1) % slides.length;
      const url = slides[nextIndex];
      if (!await preloadImage(url) || stopped) return schedule();
      const nextLayer = 1 - active;
      layers[nextLayer].style.backgroundImage = `url(${JSON.stringify(url)})`;
      requestAnimationFrame(() => {
        layers[nextLayer].classList.add('is-active');
        layers[active].classList.remove('is-active');
        active = nextLayer;
        index = nextIndex;
        queuePreload(slides[(index + 1) % slides.length]);
        schedule();
      });
    };
    const onVisibility = () => { if (document.hidden) window.clearTimeout(timer); else schedule(); };
    document.addEventListener('visibilitychange', onVisibility, {passive: true});
    queuePreload(slides[1]);
    schedule();
    slideshowCleanup = () => {
      stopped = true;
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
      layers.forEach((layer) => layer.remove());
    };
  }

  function applyDirection(host) {
    const configured = String(host?.dataset?.cpsLanguage || host?.getAttribute('lang') || 'en')
      .replace('_', '-').toLowerCase();
    const language = configured.split('-', 1)[0] || 'en';
    const direction = RTL_LANGUAGES.has(language) ? 'rtl' : 'ltr';

    host?.setAttribute('lang', configured || language);
    host?.setAttribute('dir', direction);
    document.documentElement.setAttribute('lang', configured || language);
    document.documentElement.setAttribute('dir', direction);
    document.documentElement.dataset.cpsAuthLanguage = language;
    document.documentElement.dataset.cpsAuthDirection = direction;
    if (document.body) {
      document.body.setAttribute('dir', direction);
      document.body.dataset.cpsDir = direction;
      document.body.dataset.cpsAuthLanguage = language;
    }
  }

  function applyPresentation(host) {
    const requestedShell = String(host.dataset.cpsMode || 'native').toLowerCase();
    const shellMode = ['nova', 'native'].includes(requestedShell) ? requestedShell : 'native';
    const configuredColorMode = host.dataset.cpsModeSetting || 'Light';
    const colorQuery = window.matchMedia?.('(prefers-color-scheme: dark)');
    const colorMode = resolveMode(configuredColorMode, colorQuery);
    const accent = normalizeHex(host.dataset.cpsAccent || '#7C3AED');
    const radius = Math.min(32, Math.max(8, Number(host.dataset.cpsRadius) || 22));
    const font = FONT_STACKS[host.dataset.cpsFont] || FONT_STACKS['System UI'];

    applyDirection(host);
    document.documentElement.dataset.cpsAuth = shellMode === 'native' ? '0' : '1';
    document.documentElement.dataset.cpsShellMode = shellMode;
    document.documentElement.dataset.cpsMode = colorMode;
    document.documentElement.dataset.cpsAuthFont = host.dataset.cpsFont || 'System UI';
    document.documentElement.style.setProperty('--cps-auth-primary', accent);
    document.documentElement.style.setProperty('--cps-auth-primary-rgb', colorChannels(accent));
    document.documentElement.style.setProperty('--cps-auth-primary-contrast', primaryContrast(accent));
    document.documentElement.style.setProperty('--cps-auth-primary-dark', `color-mix(in srgb, ${accent} 78%, #000)`);
    document.documentElement.style.setProperty('--cps-auth-radius', `${radius}px`);
    document.documentElement.style.setProperty('--cps-auth-font', font);

    document.body.classList.add('cps-auth-template', 'cps-auth-wrapped');
    document.body.classList.toggle('cps-login-enhanced', shellMode === 'nova');
    document.body.classList.toggle('cps-enterprise-theme', shellMode === 'nova');
    document.body.classList.toggle('cps-login-native', shellMode === 'native');
    document.body.dataset.cpsShellMode = shellMode;
    document.body.dataset.cpsMode = colorMode;
    document.body.dataset.cpsAuthVersion = VERSION;

    systemModeCleanup?.();
    systemModeCleanup = null;
    if (configuredColorMode === 'System' && colorQuery) {
      const onChange = () => {
        const nextMode = resolveMode(configuredColorMode, colorQuery);
        document.documentElement.dataset.cpsMode = nextMode;
        document.body.dataset.cpsMode = nextMode;
      };
      colorQuery.addEventListener?.('change', onChange);
      systemModeCleanup = () => colorQuery.removeEventListener?.('change', onChange);
    }

    applyBackground(shellMode === 'native' ? '' : (host.dataset.cpsBackground || ''));
    setupSlideshow(host, shellMode);
  }

  function passwordInput(toggle) {
    const selector = String(toggle?.dataset?.cpsPasswordToggle || toggle?.getAttribute('aria-controls') || '').trim();
    if (!selector) return null;
    if (selector.startsWith('#')) return document.querySelector(selector);
    return document.getElementById(selector);
  }

  function syncPasswordToggle(toggle, input = passwordInput(toggle)) {
    if (!(toggle instanceof HTMLElement) || !(input instanceof HTMLInputElement)) return;
    const shown = input.type === 'text';
    const label = translate(shown ? 'Hide password' : 'Show password');
    toggle.setAttribute('aria-pressed', shown ? 'true' : 'false');
    toggle.setAttribute('aria-label', label);
    toggle.setAttribute('title', label);
    toggle.querySelector('.cps-password-toggle-label')?.replaceChildren(document.createTextNode(label));
    const showIcon = toggle.querySelector('.cps-password-eye-show');
    const hideIcon = toggle.querySelector('.cps-password-eye-hide');
    if (showIcon) showIcon.hidden = shown;
    if (hideIcon) hideIcon.hidden = !shown;
  }

  function switchLanguage(link) {
    const language = String(link?.dataset?.cpsLoginLanguage || '').trim().toLowerCase();
    if (!language) return;
    document.cookie = `preferred_language=${encodeURIComponent(language)}; Path=/; Max-Age=31536000; SameSite=Lax`;
    const next = new URL(window.location.href);
    next.searchParams.set('_lang', language);
    window.location.assign(next.toString());
  }

  function bindInteractions() {
    eventsController?.abort();
    eventsController = new AbortController();
    const {signal} = eventsController;

    document.addEventListener('click', (event) => {
      const toggle = event.target.closest?.('[data-cps-password-toggle]');
      if (toggle) {
        event.preventDefault();
        const input = passwordInput(toggle);
        if (input) {
          const start = input.selectionStart;
          const end = input.selectionEnd;
          input.type = input.type === 'password' ? 'text' : 'password';
          syncPasswordToggle(toggle, input);
          input.focus({preventScroll: true});
          try { input.setSelectionRange(start, end); } catch (error) { /* Unsupported input state. */ }
        }
        return;
      }

      const language = event.target.closest?.('[data-cps-login-language]');
      if (language) {
        event.preventDefault();
        if (!language.classList.contains('is-active')) switchLanguage(language);
      }
    }, {signal});

    document.addEventListener('focusin', (event) => {
      event.target.closest?.('.cps-auth-field')?.classList.add('is-focused');
    }, {signal});
    document.addEventListener('focusout', (event) => {
      event.target.closest?.('.cps-auth-field')?.classList.remove('is-focused');
    }, {signal});

    document.querySelectorAll('[data-cps-password-toggle]').forEach((toggle) => syncPasswordToggle(toggle));
  }

  function enhance() {
    const host = root();
    if (!host || !document.body) return;
    applyPresentation(host);
    setModeFromHash();
    document.querySelectorAll('[data-cps-password-toggle]').forEach((toggle) => syncPasswordToggle(toggle));
  }

  function focusActivePanel() {
    requestAnimationFrame(() => requestAnimationFrame(() => {
      const panels = Array.from(document.querySelectorAll('.cps-auth-native-stack > section'));
      const active = panels.find((panel) => {
        const style = window.getComputedStyle(panel);
        return style.display !== 'none' && style.visibility !== 'hidden' && !panel.hidden;
      });
      active?.querySelector('input[autofocus], input:not([type="hidden"]):not([disabled])')?.focus?.();
    }));
  }

  function boot() {
    enhance();
    bindInteractions();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, {once: true});
  else boot();

  window.addEventListener('hashchange', () => {
    setModeFromHash();
    focusActivePanel();
  }, false);
  document.addEventListener('login_rendered', enhance, false);
  window.addEventListener('pagehide', () => {
    systemModeCleanup?.();
    slideshowCleanup?.();
    eventsController?.abort();
  }, {once: true});
})();
